import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterFormPage from "../common/MasterFormPage";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import { getDistricts } from "../../services/districtService";
import {
  createSubDivision,
  getSubDivisionById,
  SUB_DIVISION_UPDATE_SUPPORTED,
  updateSubDivision,
} from "../../services/subDivisionService";
import {
  extractApiErrorMessage,
  toSelectOptions,
} from "../../utils/masterHelpers";
import { getMasterHierarchyAccess } from "../../utils/hierarchyScope";
import "./SubDivisions.css";

const createInitialValues = () => ({
  code: "",
  district_id: "",
  name: "",
});

export default function SubDivisionForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { loading: capabilitiesLoading, user } = useCapabilities();
  const [districtOptions, setDistrictOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    open: false,
    severity: "success",
  });
  const [values, setValues] = useState(createInitialValues);
  const isEditMode = mode === "edit";
  const readOnly = isEditMode && !SUB_DIVISION_UPDATE_SUPPORTED;

  const scopeAccess = useMemo(
    () => getMasterHierarchyAccess({ user }),
    [user]
  );
  const { canSelectDistrict, fixedDistrictId } = scopeAccess;
  const effectiveDistrictId = canSelectDistrict
    ? values.district_id
    : fixedDistrictId;

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const districts = canSelectDistrict
        ? await getDistricts()
        : [];
      setDistrictOptions(toSelectOptions(districts));

      if (isEditMode) {
        if (!id) {
          setToast({
            message: "Sub-division ID is missing.",
            open: true,
            severity: "error",
          });
          return;
        }

        const selectedSubDivision = await getSubDivisionById(id);

        if (!selectedSubDivision) {
          setToast({
            message: "Sub-division not found.",
            open: true,
            severity: "error",
          });
        } else {
          setValues({
            code: selectedSubDivision.code || "",
            district_id: selectedSubDivision.district_id || "",
            name: selectedSubDivision.name || "",
          });
        }
      }
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(
          apiError,
          "Failed to load sub-division details."
        ),
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [canSelectDistrict, id, isEditMode]);

  useEffect(() => {
    if (capabilitiesLoading) {
      return;
    }

    loadData();
  }, [capabilitiesLoading, loadData]);

  useEffect(() => {
    if (isEditMode || capabilitiesLoading) {
      return;
    }

    setValues((currentValues) => ({
      ...currentValues,
      district_id: canSelectDistrict ? currentValues.district_id : fixedDistrictId,
    }));
  }, [
    capabilitiesLoading,
    canSelectDistrict,
    fixedDistrictId,
    isEditMode,
  ]);

  const fields = useMemo(
    () => [
      {
        name: "district_id",
        label: "District",
        placeholder: "Select district",
        required: true,
        type: "select",
        options: districtOptions,
        error: errors.district_id,
        hidden: !canSelectDistrict,
      },
      {
        name: "code",
        label: "Code",
        placeholder: "Enter sub-division code",
        type: "text",
        options: [],
        error: errors.code,
        hint: "Code can be left blank if the backend allows it.",
      },
      {
        name: "name",
        label: "Name",
        placeholder: "Enter sub-division name",
        required: true,
        type: "text",
        options: [],
        error: errors.name,
      },
    ],
    [
      canSelectDistrict,
      districtOptions,
      errors.code,
      errors.district_id,
      errors.name,
    ]
  );

  const validate = () => {
    const nextErrors = {};

    if (!effectiveDistrictId) {
      nextErrors.district_id = "District is required.";
    }

    if (!values.name.trim()) {
      nextErrors.name = "Sub-division name is required.";
    }

    setErrors({
      ...nextErrors,
      ...(canSelectDistrict ? {} : { district_id: "" }),
    });
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      let response;

      const payload = {
        code: values.code.trim(),
        district_id: effectiveDistrictId || null,
        name: values.name.trim(),
      };

      if (isEditMode) {
        if (!id) {
          setToast({
            message: "Sub-division ID is missing.",
            open: true,
            severity: "error",
          });
          return;
        }

        response = await updateSubDivision(id, payload);
      } else {
        response = await createSubDivision(payload);
      }

      navigate("/sub-divisions", {
        replace: true,
        state: {
          toast: {
            message:
              response?.message ||
              (isEditMode
                ? "Sub-division updated successfully."
                : "Sub-division created successfully."),
            severity: "success",
          },
        },
      });
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(
          apiError,
          isEditMode
            ? "Failed to update sub-division."
            : "Failed to create sub-division."
        ),
        open: true,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MasterShell pageClassName="sub-divisions-module">
      {loading || capabilitiesLoading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading sub-division</h2>
          <p className="masters-state__text">
            Please wait while the sub-division data is being prepared.
          </p>
        </div>
      ) : (
        <MasterFormPage
          description={
            isEditMode
              ? "Review and update the linked district and sub-division data."
              : "Create a sub-division and link it to the correct district."
          }
          fields={fields}
          onBack={() => navigate("/sub-divisions")}
          onChange={handleChange}
          onSubmit={handleSubmit}
          readOnly={readOnly}
          submitDisabled={false}
          submitLabel={isEditMode ? "Save Changes" : "Save Sub-division"}
          submitNote=""
          submitting={submitting}
          title={isEditMode ? "Edit Sub-division" : "Add Sub-division"}
          values={values}
        />
      )}

      <MasterToast
        message={toast.message}
        onClose={() =>
          setToast((currentToast) => ({ ...currentToast, open: false }))
        }
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
